import { useEffect, useMemo, useState } from "react";

import { getLegacyUrl } from "./api";

function getEntries(value) {
  if (!value || typeof value !== "object") {
    return [];
  }
  return Object.entries(value);
}

function renderCell(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

function KeyValueTable({ rows, emptyMessage, className = "" }) {
  return rows.length ? (
    <table className={`invoice-display__table ${className}`.trim()}>
      <tbody>
        {rows.map(([key, value]) => (
          <tr key={key}>
            <th>{key}</th>
            <td>{renderCell(value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  ) : (
    <p className="empty-state">{emptyMessage}</p>
  );
}

function ObjectRowsTable({ rows, emptyMessage }) {
  if (!rows?.length) {
    return <p className="empty-state">{emptyMessage}</p>;
  }

  const headers = Object.keys(rows[0] || {});

  return (
    <div className="config-table invoice-display__table-wrap">
      <table className="invoice-display__table">
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${index}-${headers.join("-")}`}>
              {headers.map((header) => (
                <td key={header}>{renderCell(row[header])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function InvoiceDisplayView({ responseFile, embedded = false, onClose = null }) {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadInvoiceDisplay() {
      if (!responseFile) {
        setError("No response file specified.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await fetch(
          `/invoice-display/?response_file=${encodeURIComponent(responseFile)}&format=json`,
          {
            credentials: "include",
          },
        );
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || "Unable to load invoice display data.");
        }

        setPayload(data.data || {});
      } catch (requestError) {
        setError(requestError.message || "Unable to load invoice display data.");
      } finally {
        setLoading(false);
      }
    }

    loadInvoiceDisplay();
  }, [responseFile]);

  const invoiceDataRows = useMemo(() => toEntries(payload?.invoice_data), [payload]);
  const checks = payload?.Checks || {};
  const gstData = payload?.gst_data || {};
  const tableData = payload?.table_data || {};
  const grnVsInvoice = payload?.grn_vs_invoice || {};
  const resultOkValues = payload?.["2b_olive_color"] || [];
  const tooltipKeys = payload?.keys_with_tooltip || [];

  function renderInvoiceChecks() {
    return (
      <div className="invoice-display__section-group">
        <section className="workspace-panel">
          <h2>Invoice Checks</h2>

          <h3 className="invoice-display__subheading">Table 1: Source - GSTN & Invoice</h3>
          {toEntries(checks.invoice_vs_gstin_protal).length ? (
            <div className="config-table invoice-display__table-wrap">
              <table className="invoice-display__table">
                <thead>
                  <tr>
                    <th>Parameters</th>
                    <th>As per Invoice</th>
                    <th>As per GST Portal</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {toEntries(checks.invoice_vs_gstin_protal).map(([key, item]) => (
                    <tr
                      key={key}
                      className={item?.Result === "Matching" ? "invoice-display__row--good" : "invoice-display__row--bad"}
                    >
                      <td>{renderCell(item?.parameter)}</td>
                      <td>{renderCell(item?.As_per_Invoice)}</td>
                      <td>
                        <div>{`Trade Name: ${renderCell(item?.As_per_GST_Portal)}`}</div>
                        {item?.As_per_GST_Portal_legal ? (
                          <div>{`Legal Name: ${renderCell(item?.As_per_GST_Portal_legal)}`}</div>
                        ) : null}
                      </td>
                      <td>{renderCell(item?.Result)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-state">No data available</p>
          )}

          <h3 className="invoice-display__subheading">Table 2A: Source - Invoice</h3>
          {toEntries(checks.invoice_validations).length ? (
            <div className="config-table invoice-display__table-wrap">
              <table className="invoice-display__table">
                <thead>
                  <tr>
                    <th>Parameters</th>
                    <th>Result</th>
                    <th>Supporting Information (As per Invoice)</th>
                  </tr>
                </thead>
                <tbody>
                  {toEntries(checks.invoice_validations).map(([key, item]) => (
                    <tr
                      key={key}
                      className={
                        resultOkValues.includes(item?.Result)
                          ? "invoice-display__row--good"
                          : "invoice-display__row--bad"
                      }
                    >
                      <td>{renderCell(item?.parameter)}</td>
                      <td>{renderCell(item?.Result)}</td>
                      <td>
                        {tooltipKeys.includes(key) ? (
                          <span className="invoice-display__tooltip" title={renderCell(item?.As_per_Invoice)}>
                            info
                          </span>
                        ) : (
                          renderCell(item?.As_per_Invoice)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-state">No data available</p>
          )}

          <h3 className="invoice-display__subheading">Table 2B: Source - GSTN Data</h3>
          {toEntries(checks.gst_portal_check).length ? (
            <div className="config-table invoice-display__table-wrap">
              <table className="invoice-display__table">
                <thead>
                  <tr>
                    <th>Parameters</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {toEntries(checks.gst_portal_check).map(([key, item]) => {
                    const rowClass = resultOkValues.includes(item?.Result)
                      ? "invoice-display__row--good"
                      : item?.Result === ""
                        ? "invoice-display__row--neutral"
                        : "invoice-display__row--bad";

                    return (
                      <tr key={key} className={rowClass}>
                        <td>{renderCell(item?.parameter)}</td>
                        <td>{renderCell(item?.Result)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-state">No data available</p>
          )}

          <h3 className="invoice-display__subheading">Table 2C: Source - Income Tax Data</h3>
          {toEntries(checks.income_tax_check).length ? (
            <div className="config-table invoice-display__table-wrap">
              <table className="invoice-display__table">
                <thead>
                  <tr>
                    <th>Parameters</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {toEntries(checks.income_tax_check).map(([key, item]) => (
                    <tr key={key}>
                      <td>{renderCell(item?.parameter)}</td>
                      <td>{renderCell(item?.Result)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-state">No data available</p>
          )}
        </section>
      </div>
    );
  }

  function renderGstData() {
    return (
      <div className="invoice-display__section-group">
        <section className="workspace-panel">
          <h2>GST Data</h2>

          <h3 className="invoice-display__subheading">Customer GST Data</h3>
          <KeyValueTable
            rows={toEntries(gstData.customer_gst_data)}
            emptyMessage="No data available, invalid GST no. or invoice does not have GST no."
          />

          <h3 className="invoice-display__subheading">Vendor GST Data</h3>
          <KeyValueTable
            rows={toEntries(gstData.vendor_gst_data)}
            emptyMessage="No data available, invalid GST no. or invoice does not have GST no."
          />

          <h3 className="invoice-display__subheading">Filing Frequency GST Portal</h3>
          {Array.isArray(payload?.Filinq_frequency) && payload.Filinq_frequency.length ? (
            <div className="config-table invoice-display__table-wrap">
              <table className="invoice-display__table">
                <thead>
                  <tr>
                    <th>quarter</th>
                    <th>preference</th>
                  </tr>
                </thead>
                <tbody>
                  {payload.Filinq_frequency.map((item, index) => (
                    <tr key={`${item.quarter}-${index}`}>
                      <td>{renderCell(item.quarter)}</td>
                      <td>{renderCell(item.preference)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-state">No data available, invalid GST no. or invoice does not have GST no.</p>
          )}

          <h3 className="invoice-display__subheading">GSTR-1 Data</h3>
          {payload?.df_gstr1_html ? (
            <div
              className="invoice-display__html-table"
              dangerouslySetInnerHTML={{ __html: payload.df_gstr1_html }}
            />
          ) : (
            <p className="empty-state">No GSTR-1 data available.</p>
          )}

          <h3 className="invoice-display__subheading">GSTR-3B Data</h3>
          {payload?.df_3b_html ? (
            <div
              className="invoice-display__html-table"
              dangerouslySetInnerHTML={{ __html: payload.df_3b_html }}
            />
          ) : (
            <p className="empty-state">No GSTR-3B data available.</p>
          )}

          <h3 className="invoice-display__subheading">Pan Details</h3>
          <KeyValueTable
            rows={toEntries(gstData.Pan_Status)}
            emptyMessage="No data available, invalid PAN no. or invoice does not have PAN no."
          />

          <h3 className="invoice-display__subheading">206AB Compliance</h3>
          <KeyValueTable
            rows={toEntries(gstData["206AB_Compliance"])}
            emptyMessage="No data available, invalid PAN no. or invoice does not have PAN no."
          />
        </section>
      </div>
    );
  }

  function renderInvoiceVsGrn() {
    return (
      <div className="invoice-display__section-group">
        <section className="workspace-panel">
          <h2>Invoice vs GRN Data</h2>

          <h3 className="invoice-display__subheading">Invoice Table</h3>
          <ObjectRowsTable rows={grnVsInvoice.invoice_data} emptyMessage="No Data Available" />

          <h3 className="invoice-display__subheading">GRN Data</h3>
          <ObjectRowsTable
            rows={grnVsInvoice.grn_data}
            emptyMessage="Invoice Id did not match any OPEN GRN data or no data available against invoice id."
          />

          <h3 className="invoice-display__subheading">Invoice vs GRN</h3>
          {toEntries(payload?.Invoicetable_vs_Grntable_compare).length ? (
            <div className="config-table invoice-display__table-wrap">
              <table className="invoice-display__table">
                <thead>
                  <tr>
                    <th>Particulars</th>
                    <th>As per Open GRN Report</th>
                    <th>As per Invoice (OCR)</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {toEntries(payload.Invoicetable_vs_Grntable_compare).map(([key, item]) => (
                    <tr key={key}>
                      <td>{renderCell(item?.Particulars)}</td>
                      <td>{renderCell(item?.As_per_grn)}</td>
                      <td>{renderCell(item?.As_per_invoice)}</td>
                      <td>{renderCell(item?.result)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-state">No data available</p>
          )}
        </section>
      </div>
    );
  }

  function renderTableData() {
    return (
      <div className="invoice-display__section-group">
        <section className="workspace-panel">
          <h2>Table Data</h2>

          <h3 className="invoice-display__subheading">Table</h3>
          <ObjectRowsTable rows={tableData.tabledata_} emptyMessage="No Data Available" />

          <h3 className="invoice-display__subheading">Check2</h3>
          <KeyValueTable rows={toEntries(tableData.check_2)} emptyMessage="No Data Available" />

          <h3 className="invoice-display__subheading">Check3</h3>
          <KeyValueTable rows={toEntries(tableData.check_3)} emptyMessage="No Data Available" />
        </section>
      </div>
    );
  }

  function renderInvoiceData() {
    return (
      <div className="invoice-display__section-group">
        <section className="workspace-panel">
          <h2>Invoice Data</h2>
          <KeyValueTable rows={invoiceDataRows} emptyMessage="No invoice data available." />
        </section>
      </div>
    );
  }

  function renderAllSections() {
    return (
      <>
        {renderInvoiceChecks()}
        {renderGstData()}
        {renderInvoiceVsGrn()}
        {renderTableData()}
        {renderInvoiceData()}
      </>
    );
  }

  return (
    <main className={`invoice-display${embedded ? " invoice-display--embedded" : ""}`}>
      {embedded ? (
        <>
          <header className="invoice-display__topbar">
            <div>
              <p className="eyebrow">Invoice Display</p>
              <h1>{responseFile || "Invoice response"}</h1>
              <p className="config-note">
                Review invoice checks, GST data, GRN comparisons, extracted table data, and raw invoice
                details from the existing Django backend response file.
              </p>
            </div>

            <div className="invoice-display__actions">
              <button className="ghost-button" type="button" onClick={onClose}>
                Close
              </button>
            </div>
          </header>

          {loading ? (
            <section className="workspace-panel">
              <p className="config-note">Loading invoice display…</p>
            </section>
          ) : error ? (
            <section className="workspace-panel">
              <div className="config-banner config-banner--error">{error}</div>
            </section>
          ) : (
            renderAllSections()
          )}
        </>
      ) : (
        <div className="bridge-card">
          <h1>Invoice Display</h1>
          <p>
            Review invoice checks, GST data, GRN comparisons, extracted table data, and raw invoice
            details from the existing Django backend response file.
          </p>

          {loading ? (
            <p>Loading invoice display…</p>
          ) : error ? (
            <div style={{ color: 'red' }}>{error}</div>
          ) : (
            renderAllSections()
          )}
        </div>
      )}
    </main>
  );
}
